"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ChevronRight, Loader, Send, ShieldCheck, User, Activity, GraduationCap, Video } from "lucide-react";
const inputBaseClass = "flex h-11 w-full rounded-none border border-input/50 bg-background/40 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 backdrop-blur-md transition-all duration-200";
const selectBaseClass = "flex h-11 w-full rounded-none border border-input/50 bg-background/40 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 backdrop-blur-md transition-all duration-200 appearance-none";
const textareaBaseClass = "flex min-h-[120px] w-full rounded-none border border-input/50 bg-background/40 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 backdrop-blur-md transition-all duration-200 resize-y";
export default function ProspectProfileForm() {
  return <section id="prospect-profile-form" className="dark bg-background text-foreground relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_0%_0%,_var(--primary)_0%,_transparent_25%)] opacity-10" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_100%_100%,_var(--primary)_0%,_transparent_25%)] opacity-10" />
        {}
        <div className="absolute top-1/4 -right-64 w-128 h-8 bg-primary/5 -rotate-45 transform origin-center" />
        <div className="absolute bottom-1/4 -left-64 w-128 h-8 bg-primary/5 -rotate-45 transform origin-center" />
      </div>

      <div className="relative max-w-4xl mx-auto">
        <div className="text-center mb-12">
          
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-sans mb-4">
            Prospect Profile <span className="text-primary">Application</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Submit detailed athletic and academic information for advancement consideration with DB Hockey Advancement.
          </p>
        </div>

        <div className="bg-card/30 backdrop-blur-xl border border-border/50 p-1 md:p-8 shadow-2xl relative">
          {}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary" />

          <FormProvider formId="prospect_profile_form_player_inquiry" sectionName="Prospect Profile Form" defaultValues={{
          contact_name: "",
          email: "",
          phone: "",
          location: "",
          player_name: "",
          birth_year: "",
          height: "",
          weight: "",
          position: "",
          shoots: "Left",
          current_team: "",
          current_league: "",
          previous_teams: "",
          graduation_year: "",
          goals: "",
          elite_prospects_url: "",
          highlight_video_url: "",
          message: ""
        }} validationRules={{
          contact_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          location: {
            required: true
          },
          player_name: {
            required: true
          },
          birth_year: {
            required: true,
            min: 1990,
            max: 2025
          },
          height: {
            min: 30,
            max: 96
          },
          weight: {
            min: 50,
            max: 300
          },
          position: {
            required: true
          },
          shoots: {
            required: true
          },
          current_team: {
            required: true
          },
          current_league: {
            required: true
          },
          graduation_year: {
            required: true,
            min: 2020,
            max: 2030
          },
          goals: {
            required: true
          },
          elite_prospects_url: {
            pattern: "url"
          },
          highlight_video_url: {
            pattern: "url"
          }
        }} submitText="Submit Profile" successMessage="Profile submitted successfully. Our scouting team will review your information and contact you soon.">
            <ProspectFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ProspectFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [activeTab, setActiveTab] = useState("personal");
  if (isSubmitted && !submitError) {
    return <div className="py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 text-primary mb-4">
          <ShieldCheck className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Application Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto text-lg">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto p-1 bg-background/50 border border-border/50 rounded-none mb-8 gap-1">
          <TabsTrigger value="personal" className="rounded-none py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
            <User className="w-4 h-4 mr-2 hidden sm:inline-block" />
            Personal Info
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-none py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
            <Activity className="w-4 h-4 mr-2 hidden sm:inline-block" />
            Playing History
          </TabsTrigger>
          <TabsTrigger value="academics" className="rounded-none py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
            <GraduationCap className="w-4 h-4 mr-2 hidden sm:inline-block" />
            Academics & Goals
          </TabsTrigger>
          <TabsTrigger value="media" className="rounded-none py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
            <Video className="w-4 h-4 mr-2 hidden sm:inline-block" />
            Media & Links
          </TabsTrigger>
        </TabsList>

        <div className="min-h-[400px]">
          {}
          <TabsContent value="personal" className="space-y-6 mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="contact_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Your Name *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="John Doe" className={inputBaseClass} />
                    </FormControl>
                    <FormDescription>First and last name of the parent or player.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Email Address *</FormLabel>
                    <FormControl>
                      <input {...field} type="email" placeholder="john@example.com" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Phone Number *</FormLabel>
                    <FormControl>
                      <input {...field} type="tel" placeholder="(555) 123-4567" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="location" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Location *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="City, State" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="border-t border-border/50 pt-6 mt-6">
              <h4 className="text-lg font-semibold mb-4 text-primary">Player Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="player_name" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Player Name *</FormLabel>
                      <FormControl>
                        <input {...field} type="text" placeholder="Jane Smith" className={inputBaseClass} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="birth_year" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Birth Year *</FormLabel>
                      <FormControl>
                        <input {...field} type="number" placeholder="2005" className={inputBaseClass} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="height" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Height (inches)</FormLabel>
                      <FormControl>
                        <input {...field} type="number" placeholder="68" className={inputBaseClass} />
                      </FormControl>
                      <FormDescription>Enter height in total inches (e.g., 5'10" = 70).</FormDescription>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="weight" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Weight (lb)</FormLabel>
                      <FormControl>
                        <input {...field} type="number" placeholder="150" className={inputBaseClass} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button type="button" onClick={() => setActiveTab("history")} className="rounded-none px-8">
                Next Step <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="history" className="space-y-6 mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="position" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Primary Position *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <select {...field} className={selectBaseClass}>
                          <option value="" disabled>Select position</option>
                          {["Forward", "Defense", "Goalie"].map((opt, i) => <option key={opt} value={opt} data-index={i}>{opt}</option>)}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <ChevronRight className="w-4 h-4 text-muted-foreground rotate-90" />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="shoots" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Shoots *</FormLabel>
                    <FormControl>
                      <div className="flex gap-6 h-11 items-center">
                        {["Left", "Right"].map((opt, i) => <label key={opt} data-index={i} className="flex items-center gap-2 cursor-pointer group">
                            <div className="relative flex items-center justify-center w-5 h-5 border border-input bg-background/50 rounded-full group-hover:border-primary transition-colors">
                              <input type="radio" {...field} value={opt} checked={field.value === opt} className="absolute opacity-0 w-full h-full cursor-pointer" />
                              {field.value === opt && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                            </div>
                            <span className="text-sm">{opt}</span>
                          </label>)}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="current_team" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Current Team *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="Team Name" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="current_league" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Current League *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="League Name" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <FormField control={control} name="previous_teams" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Previous Teams (optional)</FormLabel>
                  <FormControl>
                    <textarea {...field} placeholder="List any previous teams, one per line" className={textareaBaseClass} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab("personal")} className="rounded-none">
                Back
              </Button>
              <Button type="button" onClick={() => setActiveTab("academics")} className="rounded-none px-8">
                Next Step <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="academics" className="space-y-6 mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="graduation_year" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">High School Graduation Year *</FormLabel>
                    <FormControl>
                      <input {...field} type="number" placeholder="2024" className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="goals" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Future Goals *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <select {...field} className={selectBaseClass}>
                          <option value="" disabled>Select goal</option>
                          {["Junior Hockey", "College Hockey", "Both"].map((opt, i) => <option key={opt} value={opt} data-index={i}>{opt}</option>)}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <ChevronRight className="w-4 h-4 text-muted-foreground rotate-90" />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab("history")} className="rounded-none">
                Back
              </Button>
              <Button type="button" onClick={() => setActiveTab("media")} className="rounded-none px-8">
                Next Step <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="media" className="space-y-6 mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="elite_prospects_url" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Elite Prospects Profile URL</FormLabel>
                    <FormControl>
                      <input {...field} type="url" placeholder="https://www.eliteprospects.com/player/..." className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="highlight_video_url" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Highlight Video URL</FormLabel>
                    <FormControl>
                      <input {...field} type="url" placeholder="https://www.youtube.com/watch?v=..." className={inputBaseClass} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <FormField control={control} name="message" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground/80 uppercase tracking-wider text-xs font-semibold">Additional Questions / Message</FormLabel>
                  <FormControl>
                    <textarea {...field} placeholder="Enter any questions or additional information here..." className={textareaBaseClass} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {submitError}
              </div>}

            <div className="flex justify-between pt-8 border-t border-border/50">
              <Button type="button" variant="outline" onClick={() => setActiveTab("academics")} className="rounded-none">
                Back
              </Button>
              <Button type="submit" disabled={isSubmitting} className="rounded-none px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] hover:translate-y-[2px] hover:translate-x-[2px] transition-all">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 w-4 h-4" />
                    Submitting...
                  </> : <>
                    Submit Profile <Send className="ml-2 w-4 h-4" />
                  </>}
              </Button>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </Form>;
}